// MapLibre まわりをまとめた層。レイヤ ID・feature-state・ハザードラスタの重ね順といった
// 「地図の都合」をここに閉じ、main.ts からは市区町村コードと色付き GeoJSON だけを渡す。
import { Map as MapLibreMap, Marker, NavigationControl, Popup, setWorkerUrl } from "maplibre-gl";
import type { ExpressionSpecification, GeoJSONSource } from "maplibre-gl";

const MUNI_SOURCE = "municipalities";
const MUNI_LAYER = "municipalities-circle";

/** id はハザードマップポータルのレイヤと UI ラベル（i18n の hazardLayer）を結ぶ鍵。 */
export const HAZARD_LAYERS: { id: string; layer: string }[] = [
  { id: "flood", layer: "01_flood_l2_shinsuishin_data" },
  { id: "tsunami", layer: "04_tsunami_newlegend_data" },
  { id: "sediment-flow", layer: "05_dosekiryukeikaikuiki" },
  { id: "sediment-steep", layer: "05_kyukeishakeikaikuiki" },
];

const JAPAN_BOUNDS: [[number, number], [number, number]] = [
  [122.9, 24.0],
  [146.0, 45.6],
];

export const SELECT_ZOOM = 10;
export const LIST_CLICK_MIN_ZOOM = 9;
const HAZARD_RASTER_OPACITY = 0.55;

/** dark テーマでは地理院 pale が明るすぎるので、raster paint を落として地図面を沈ませる。 */
const DARK_BASE_PAINT = {
  "raster-brightness-max": 0.6,
  "raster-saturation": -0.35,
  "raster-contrast": 0.15,
};
const LIGHT_BASE_PAINT = {
  "raster-brightness-max": 1,
  "raster-saturation": 0,
  "raster-contrast": 0,
};

const ACTIVE_SCALE = 1.5;
const IS_ACTIVE: ExpressionSpecification = [
  "any",
  ["boolean", ["feature-state", "hover"], false],
  ["boolean", ["feature-state", "selected"], false],
];

/** ある zoom での円の半径（px）。hover / selected 時は ACTIVE_SCALE 倍。 */
function radiusAt(radius: number): ExpressionSpecification {
  return ["case", IS_ACTIVE, radius * ACTIVE_SCALE, radius];
}

function hazardTileUrl(layer: string): string {
  return `https://disaportaldata.gsi.go.jp/raster/${layer}/{z}/{x}/{y}.png`;
}

/** 浮いているパネルに隠れない地図の可視領域。fitBounds / flyTo の中心はこの内側に来る。 */
export interface MapPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface MapViewOptions {
  container: string;
  padding(): MapPadding;
  onSelect(code: string): void;
  onHover(code: string | null): void;
  /** hover popup の中身（既にエスケープ済みの HTML）。 */
  describeHover(code: string): string;
}

export interface MapView {
  setData(data: GeoJSON.FeatureCollection): void;
  setHovered(code: string | null): void;
  setSelected(code: string | null, lonlat: [number, number] | null): void;
  flyTo(lonlat: [number, number], minZoom: number): void;
  setHazardVisible(id: string, visible: boolean): void;
  setTheme(theme: "light" | "dark"): void;
  resize(): void;
}

export function createMapView(opts: MapViewOptions): MapView {
  setWorkerUrl("/maplibre-gl-worker.mjs");

  const map = new MapLibreMap({
    container: opts.container,
    style: {
      version: 8,
      sources: {
        pale: {
          type: "raster",
          tiles: ["https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png"],
          tileSize: 256,
          attribution: "地理院タイル",
        },
      },
      layers: [{ id: "pale", type: "raster", source: "pale" }],
    },
    bounds: JAPAN_BOUNDS,
    fitBoundsOptions: { padding: opts.padding() },
    attributionControl: { compact: true },
  });
  map.addControl(new NavigationControl({ showCompass: false }), "bottom-right");

  const popup = new Popup({ closeButton: false, closeOnClick: false, offset: 12, className: "muni-tip" });
  const pulse = document.createElement("div");
  pulse.className = "pulse-ring";
  const marker = new Marker({ element: pulse });

  let hovered: string | null = null;
  let selected: string | null = null;
  let theme: "light" | "dark" = "light";
  let pending: GeoJSON.FeatureCollection | null = null;
  let ready = false;
  const hazardVisible = new Map<string, boolean>();

  function setFeatureState(code: string | null, key: "hover" | "selected", value: boolean): void {
    if (code === null || !ready) return;
    map.setFeatureState({ source: MUNI_SOURCE, id: code }, { [key]: value });
  }

  function strokeColor(): string {
    return theme === "dark" ? "#0b1220" : "#ffffff";
  }

  /** hover の feature-state は地図側の mousemove と外部（一覧 hover）の両方から来る。 */
  function applyHover(code: string | null): void {
    if (code === hovered) return;
    setFeatureState(hovered, "hover", false);
    hovered = code;
    setFeatureState(hovered, "hover", true);
  }

  function install(data: GeoJSON.FeatureCollection): void {
    map.addSource(MUNI_SOURCE, { type: "geojson", data, promoteId: "code" });
    map.addLayer({
      id: MUNI_LAYER,
      type: "circle",
      source: MUNI_SOURCE,
      paint: {
        // "zoom" は top-level の interpolate にしか置けないので、hover 拡大は各 stop の出力側で分岐する
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, radiusAt(3.5), 6, radiusAt(5), 8, radiusAt(7), 11, radiusAt(10)],
        "circle-color": ["get", "color"],
        "circle-opacity": ["case", ["get", "hasValue"], 1, 0.55],
        "circle-stroke-color": strokeColor(),
        // 全国表示では円が密集するので縁を細くし、寄るほど太くして輪郭を立てる
        "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 4, ["case", IS_ACTIVE, 2, 0.8], 9, ["case", IS_ACTIVE, 2.5, 1.5]],
        "circle-stroke-opacity": 0.9,
      },
    });

    for (const h of HAZARD_LAYERS) {
      map.addSource(`hazard-${h.id}`, {
        type: "raster",
        tiles: [hazardTileUrl(h.layer)],
        tileSize: 256,
        attribution: "ハザードマップポータルサイト",
      });
      map.addLayer(
        {
          id: `hazard-${h.id}-layer`,
          type: "raster",
          source: `hazard-${h.id}`,
          paint: { "raster-opacity": HAZARD_RASTER_OPACITY },
          layout: { visibility: hazardVisible.get(h.id) ? "visible" : "none" },
        },
        MUNI_LAYER,
      );
    }

    map.on("mousemove", MUNI_LAYER, (e) => {
      const code = e.features?.[0]?.properties?.["code"];
      if (typeof code !== "string") return;
      map.getCanvas().style.cursor = "pointer";
      if (code !== hovered) {
        applyHover(code);
        opts.onHover(code);
      }
      popup.setLngLat(e.lngLat).setHTML(opts.describeHover(code)).addTo(map);
    });
    map.on("mouseleave", MUNI_LAYER, () => {
      map.getCanvas().style.cursor = "";
      popup.remove();
      applyHover(null);
      opts.onHover(null);
    });
    map.on("click", MUNI_LAYER, (e) => {
      const code = e.features?.[0]?.properties?.["code"];
      if (typeof code === "string") opts.onSelect(code);
    });

    ready = true;
    // レイヤ設置前に届いていた選択・hover を反映し直す
    setFeatureState(hovered, "hover", true);
    setFeatureState(selected, "selected", true);
  }

  map.on("load", () => {
    if (pending) {
      install(pending);
      pending = null;
    }
    applyTheme();
  });

  function applyTheme(): void {
    if (!map.getLayer("pale")) return;
    const paint = theme === "dark" ? DARK_BASE_PAINT : LIGHT_BASE_PAINT;
    for (const [key, value] of Object.entries(paint)) {
      map.setPaintProperty("pale", key as keyof typeof paint, value);
    }
    if (map.getLayer(MUNI_LAYER)) map.setPaintProperty(MUNI_LAYER, "circle-stroke-color", strokeColor());
  }

  map.on("error", (e) => console.error("map error", e.error));

  return {
    setData(data) {
      const source = map.getSource(MUNI_SOURCE) as GeoJSONSource | undefined;
      if (source) source.setData(data);
      else if (map.isStyleLoaded() && map.loaded()) install(data);
      else pending = data;
    },
    setHovered: applyHover,
    setSelected(code, lonlat) {
      setFeatureState(selected, "selected", false);
      selected = code;
      setFeatureState(selected, "selected", true);
      if (code !== null && lonlat !== null) marker.setLngLat(lonlat).addTo(map);
      else marker.remove();
    },
    flyTo(lonlat, minZoom) {
      map.flyTo({ center: lonlat, zoom: Math.max(map.getZoom(), minZoom), speed: 1.1, padding: opts.padding() });
    },
    setHazardVisible(id, visible) {
      hazardVisible.set(id, visible);
      const layerId = `hazard-${id}-layer`;
      if (map.getLayer(layerId)) map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
    },
    setTheme(next) {
      theme = next;
      applyTheme();
    },
    resize() {
      map.resize();
    },
  };
}
